/**
 * DAY BRIEF — the glance home's one derived narrative sentence
 * (docs/design/reimagine-glance-home.md "The glance home", item 3): "≤14
 * words, directly under the hero — the concierge/EA voice that turns a
 * schedule into a morning brief. NOT freeform AI at display time (trust
 * law): a template filled from the resolved day-chain (event count + 2-3
 * anchor times + tonight's hotel)."
 *
 * This is a TEMPLATE BANK, not generation: buildDayBrief picks one of a
 * small fixed set of i18n message keys based on the day's shape (quiet /
 * travel / normal) and fills it with digits + place/title strings already
 * present on the entries — never invents wording. Pure logic, no React;
 * gaps.test.ts's sibling day-brief.test.ts locks the picks down. All
 * collection/field names come from the registry by typed property access
 * (CONSTRAINTS #5 — no name literals here).
 */
import { registry } from "@td/registry";
import type { GigRecord } from "../db/idb.ts";
import { t, type Locale } from "../i18n.ts";
import type { DayRunEntry } from "./dayRun.ts";

function s(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** A short human label for a dated entry — its own title/act/route text,
 * never an invented category word (schedule titles are free text: "Balance",
 * "Set", "Lobby call" already ARE the anchor vocabulary the design doc's
 * example uses). Registry typed access per collection. */
function anchorLabel(entry: DayRunEntry): string {
  const key = entry.collection;
  if (key === registry.schedule.key) {
    return s(entry.item.fields[registry.schedule.fields.title.key]);
  }
  if (key === registry.timetable.key) {
    return s(entry.item.fields[registry.timetable.fields.act.key]);
  }
  if (key === registry.travel.key) {
    const tv = registry.travel.fields;
    const dep = s(entry.item.fields[tv.dep_place.key]) || s(entry.item.fields[tv.dep_iata.key]);
    const arr = s(entry.item.fields[tv.arr_place.key]) || s(entry.item.fields[tv.arr_iata.key]);
    const route = [dep, arr].filter(Boolean).join(" → ");
    return route || s(entry.item.fields[tv.kind.key]);
  }
  if (key === registry.hotels.key) {
    return s(entry.item.fields[registry.hotels.fields.name.key]);
  }
  return "";
}

/** Tonight's hotel name: a check-in among `entries` (already scoped to one
 * day by the caller — see buildDayBrief). */
function tonightHotel(entries: readonly DayRunEntry[]): string {
  const checkIn = entries.find((e) => e.collection === registry.hotels.key && e.hotelPhase === "check_in");
  if (checkIn !== undefined) return s(checkIn.item.fields[registry.hotels.fields.name.key]);
  return "";
}

/**
 * The one derived narrative sentence for a SINGLE day's entries (the caller
 * scopes `entries` to one day-run group, e.g. `buildDayRun(gig)[i].entries` —
 * mirrors buildGaps taking the chain it operates over rather than re-deriving
 * "today" itself). ≤~14 words, FR-first natural phrasing, numbers as digits.
 * `gig` is accepted for signature parity with the design doc and headroom
 * for a future gig-scoped pick (e.g. per-person anchors); today's picks read
 * entirely off `entries`.
 */
export function buildDayBrief(entries: readonly DayRunEntry[], _gig: GigRecord, locale: Locale): string {
  // Commitments = timed, NON-hotel entries. A hotel check-in is "tonight's
  // hotel" (the "nuit au X" tail), never a "temps"/anchor — so it must not
  // inflate the count, block the quiet-day branch, or break travel-only.
  const timed = entries
    .filter((e) => e.time !== "" && e.collection !== registry.hotels.key)
    .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
  const hotel = tonightHotel(entries);

  if (timed.length === 0) {
    return hotel !== ""
      ? t(locale, "day_brief.quiet_with_hotel", { hotel })
      : t(locale, "day_brief.quiet");
  }

  const travelOnly = timed.every((e) => e.collection === registry.travel.key);
  // Up to 3 anchors: earliest, a middle one (if any), and the latest — the
  // "2-3 anchor times" the design doc calls for, never the whole list.
  const anchors: DayRunEntry[] =
    timed.length <= 3 ? timed : [timed[0]!, timed[Math.floor(timed.length / 2)]!, timed[timed.length - 1]!];
  const anchorPhrases = anchors
    .map((e) => {
      const label = anchorLabel(e);
      return label !== "" ? `${label} ${e.time}` : e.time;
    })
    .filter((p) => p !== "");

  if (travelOnly && anchorPhrases.length > 0) {
    return hotel !== ""
      ? t(locale, "day_brief.travel_with_hotel", { anchors: anchorPhrases.join(", "), hotel })
      : t(locale, "day_brief.travel", { anchors: anchorPhrases.join(", ") });
  }

  const params = { n: timed.length, anchors: anchorPhrases.join(", ") };
  return hotel !== ""
    ? t(locale, "day_brief.normal_with_hotel", { ...params, hotel })
    : t(locale, "day_brief.normal", params);
}
